/** @jsxImportSource @emotion/react */
import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import logo from '../../assets/logo.svg';
import { translations } from '../../../utils/translations';

// Video URLs hosted on Cloudflare R2
const introVideo = 'https://onboarding.shortsleaderboard.com/videos/spicy_introvid.mov';
const hooksVideo = 'https://onboarding.shortsleaderboard.com/videos/spicy_basicsandhooks.mp4';
const ctaVideo = 'https://onboarding.shortsleaderboard.com/videos/spicy_cta.mp4';
import { 
  Lock, 
  PlayCircle, 
  CheckCircle, 
  Circle, 
  ChevronRight, 
  AlertCircle,
  ArrowUp,
  Menu,
  X
} from 'lucide-react';

function ContentBriefUnifiedComponent() {
  const [searchParams] = useSearchParams();
  const lang = searchParams.get('lang') === 'es' ? 'es' : 'en';
  const t = useMemo(() => translations[lang].contentBrief, [lang]);
  
  const [password, setPassword] = useState('');
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [completedItems, setCompletedItems] = useState<string[]>([]);
  const [activeSection, setActiveSection] = useState('');
  const [showMobileMenu, setShowMobileMenu] = useState(false);

  // Refs for sections
  const accountSetupRef = useRef<HTMLDivElement>(null);
  const introVideoRef = useRef<HTMLDivElement>(null);
  const hooksFormulaRef = useRef<HTMLDivElement>(null);
  const trainingVideosRef = useRef<HTMLDivElement>(null);
  const firstDraftRef = useRef<HTMLDivElement>(null);
  const postingRef = useRef<HTMLDivElement>(null);
  const engagementRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const storedToken = localStorage.getItem('contentBriefToken');
    if (storedToken === 'spicypeach') {
      setIsAuthenticated(true);
    }

    // Load completed items from localStorage
    const saved = localStorage.getItem('completedChecklistItems');
    if (saved) {
      setCompletedItems(JSON.parse(saved));
    }
  }, []);

  useEffect(() => {
    // Save completed items to localStorage
    localStorage.setItem('completedChecklistItems', JSON.stringify(completedItems));
  }, [completedItems]);

  // Track scroll position for active section
  useEffect(() => {
    const handleScroll = () => {
      const sections = [
        { id: 'setup-accounts', ref: accountSetupRef },
        { id: 'intro-video', ref: introVideoRef },
        { id: 'hooks-formula', ref: hooksFormulaRef },
        { id: 'training-videos', ref: trainingVideosRef },
        { id: 'first-draft', ref: firstDraftRef },
        { id: 'posting', ref: postingRef },
        { id: 'engagement', ref: engagementRef },
      ];

      const scrollPosition = window.scrollY + 100;

      for (const section of sections) {
        if (section.ref.current) {
          const { offsetTop, offsetHeight } = section.ref.current;
          if (scrollPosition >= offsetTop && scrollPosition < offsetTop + offsetHeight) {
            setActiveSection(section.id);
            break;
          }
        }
      }
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const handlePasswordSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (password === 'spicypeach') {
      localStorage.setItem('contentBriefToken', 'spicypeach');
      setIsAuthenticated(true);
      setPassword('');
    } else {
      alert(lang === 'es' ? 'Contraseña incorrecta' : 'Incorrect password');
      setPassword('');
    }
  };

  const toggleComplete = (id: string) => {
    setCompletedItems(prev => 
      prev.includes(id) 
        ? prev.filter(item => item !== id)
        : [...prev, id]
    );
  };

  const scrollToSection = (ref: React.RefObject<HTMLDivElement>) => {
    ref.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    setShowMobileMenu(false);
  };

  const checklistItems = [
    { 
      id: 'setup-accounts', 
      label: t.checklistItems[0],
      ref: accountSetupRef,
      action: () => scrollToSection(accountSetupRef)
    },
    { 
      id: 'watch-intro', 
      label: t.checklistItems[1],
      ref: introVideoRef,
      action: () => scrollToSection(introVideoRef)
    },
    { 
      id: 'learn-hooks', 
      label: t.checklistItems[2],
      ref: hooksFormulaRef,
      action: () => scrollToSection(hooksFormulaRef)
    },
    { 
      id: 'watch-training', 
      label: t.checklistItems[3],
      ref: trainingVideosRef,
      action: () => scrollToSection(trainingVideosRef)
    },
    { 
      id: 'first-draft', 
      label: t.checklistItems[4],
      ref: firstDraftRef,
      action: () => scrollToSection(firstDraftRef)
    },
    { 
      id: 'start-posting', 
      label: t.checklistItems[5],
      ref: postingRef,
      action: () => scrollToSection(postingRef)
    },
    { 
      id: 'engage', 
      label: t.checklistItems[6],
      ref: engagementRef,
      action: () => scrollToSection(engagementRef)
    },
  ];

  if (!isAuthenticated) {
    return (
      <div className="min-h-screen bg-[#7B1C14] py-12 px-4 sm:px-6 lg:px-8 font-sans">
        <div className="max-w-md mx-auto">
          <div className="text-center mb-8">
            <Lock className="mx-auto h-12 w-12 text-[#F7E8CC]" />
            <h1 className="mt-6 text-3xl font-bold text-[#F7E8CC]">
              {t.passwordTitle}
            </h1>
          </div>
          <form onSubmit={handlePasswordSubmit} className="mt-8 space-y-6">
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="appearance-none rounded relative block w-full px-3 py-2 border border-[#F7E8CC]/20 bg-[#6E1912] placeholder-[#F7E8CC]/50 text-[#F7E8CC] focus:outline-none focus:ring-2 focus:ring-[#F7E8CC]/50"
              placeholder={t.passwordPrompt}
            />
            <button
              type="submit"
              className="w-full py-2 px-4 border border-transparent text-sm font-medium rounded-md text-[#7B1C14] bg-[#F7E8CC] hover:bg-[#F7E8CC]/90"
            >
              {t.accessButton}
            </button>
          </form>
        </div>
      </div>
    );
  }

  // Helper function for warm-up steps content
  const getWarmUpStepContent = (stepNum: number) => {
    if (lang === 'es') {
      switch(stepNum) {
        case 1:
          return (
            <div>
              <strong> Estudia e interactúa durante 3 días:</strong> 
              <br />
              <br />
              Ve al canal de <strong>Inspo</strong> en Slack. Mira compulsivamente el contenido enlazado en el canal (Ashli siempre está agregando contenido fresco aquí). Sigue al menos a 10 creadores.
              Dale me gusta, republica y participa en sus comentarios. Estúdialos, toma notas sobre su contenido y trata de descubrir por qué se volvieron virales.
              <br />
              <br />
              Este es tu curso de maestría en creación de contenido. Al hacer esto durante los próximos 5 días antes de publicar, le estás diciendo a TikTok que eres HUMANO y no un bot.
              También estás aprendiendo directamente de los mejores del negocio. Muy pocas personas se toman el tiempo para hacer esto, probablemente hayas visto este tipo de contenido
              pero ¿alguna vez te has tomado el tiempo para estudiarlo? Si tomas esta parte en serio, estarás alcanzando meses de $5k, $10k, incluso $25k+ (pregúntale a Ashli, Pamela, Kamila, Giovanna, la lista continúa).
              <br />
              <br />
              <strong>IMPORTANTE:</strong> Debes hacer esto durante al menos 3 días antes de publicar tu primer video para evitar el shadowban.
            </div>
          );
        case 2:
          return (
            <div>
              <strong>Haz borradores para los primeros 5 videos:</strong> Sigue exactamente nuestros guiones proporcionados
              <a href="https://docs.google.com/document/d/1BUZY-DdhM1PzTQZNFHF0ejnoRNvAel3CNnw9m6RAazE/edit?tab=t.0" 
                 target="_blank" 
                 rel="noopener noreferrer" 
                 className="text-[#FFD700] hover:underline ml-2">
                {t.viewScripts}
              </a>
            </div>
          );
        case 3:
          return (
            <div>
              <strong>Publica consistentemente:</strong> 1-2 videos no promocionales diarios durante el calentamiento
            </div>
          );
        case 4:
          return (
            <div>
              <strong>Obtén aprobación:</strong> Envía tus primeros videos de borrador a Ashli en Slack antes de publicar
            </div>
          );
        default:
          return null;
      }
    } else {
      switch(stepNum) {
        case 1:
          return (
            <div>
              <strong> Study and engage for 3 days:</strong> 
              <br />
              <br />
              Go to the <strong>Inspo</strong> channel on Slack. Binge watch the content linked in the channel (Ashli is always adding fresh content here). Follow at least 10 creators. 
              Like, repost, and engage in their comments. Study them, take notes on their content and try to figure out why they went viral.
              <br />
              <br />
              This is your masters course in content creation. By doing this for the next 5 days before posting, you are letting TikTok know that you are a HUMAN and not a bot.
              You are also learning directly from the best in the business. Very few people take the time to do this, you have probably doomscrolled to this kind of content
              but have you ever taken the time to study it? If you take this part seriously, you will be hitting $5k, $10k, even $25k+ months (ask Ashli, Pamela, Kamila, Giovanna, the list goes on).
              <br />
              <br />
              <strong>IMPORTANT:</strong> You must do this for at least 3 days before posting your first video to avoid getting shadowbanned.
            </div>
          );
        case 2:
          return (
            <div>
              <strong>Make Drafts for First 5 Videos:</strong> Follow our provided scripts exactly
              <a href="https://docs.google.com/document/d/1BUZY-DdhM1PzTQZNFHF0ejnoRNvAel3CNnw9m6RAazE/edit?tab=t.0" 
                 target="_blank" 
                 rel="noopener noreferrer" 
                 className="text-[#FFD700] hover:underline ml-2">
                View Scripts →
              </a>
            </div>
          );
        case 3:
          return (
            <div>
              <strong>Post Consistently:</strong> 1-2 non-promotional videos daily during warm-up
            </div>
          );
        case 4:
          return (
            <div>
              <strong>Get Approval:</strong> Send your first draft videos to Ashli on Slack before posting
            </div>
          );
        default:
          return null;
      }
    }
  };

  return (
    <div className="min-h-screen bg-[#7B1C14] font-sans">
      {/* Sticky Header */}
      <div className="sticky top-0 z-50 bg-[#6E1912] border-b border-[#5E150F] shadow-lg">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <img src={logo} alt="Spicy Cubes" className="h-10" />
            
            {/* Desktop Navigation */}
            <nav className="hidden md:flex items-center space-x-6">
              {checklistItems.map(item => (
                <button
                  key={item.id}
                  onClick={item.action}
                  className={`text-sm font-medium transition-colors ${
                    activeSection === item.id.replace('watch-', '').replace('learn-', '').replace('start-', '')
                      ? 'text-[#FFD700]' 
                      : 'text-[#F7E8CC]/70 hover:text-[#F7E8CC]'
                  }`}
                >
                  {completedItems.includes(item.id) ? (
                    <CheckCircle className="h-4 w-4 inline mr-1" />
                  ) : (
                    <Circle className="h-4 w-4 inline mr-1" />
                  )}
                  {item.label.split(' ').slice(0, 2).join(' ')}
                </button>
              ))}
            </nav>

            {/* Progress Indicator */}
            <div className="hidden md:block text-right">
              <p className="text-[#F7E8CC]/60 text-xs">{t.progress}</p>
              <p className="text-[#FFD700] font-bold text-sm">
                {completedItems.length}/7
              </p>
            </div>

            {/* Mobile Menu Button */}
            <button
              onClick={() => setShowMobileMenu(!showMobileMenu)}
              className="md:hidden p-2 text-[#F7E8CC]"
            >
              {showMobileMenu ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
            </button>
          </div>
        </div>

        {/* Mobile Navigation */}
        {showMobileMenu && (
          <div className="md:hidden bg-[#5E150F] border-t border-[#4A110C]">
            <nav className="px-4 py-3 space-y-2">
              {checklistItems.map(item => (
                <button
                  key={item.id}
                  onClick={item.action}
                  className="w-full text-left px-3 py-2 text-sm text-[#F7E8CC]/90 hover:bg-[#4A110C]/50 rounded flex items-center"
                >
                  {completedItems.includes(item.id) ? (
                    <CheckCircle className="h-4 w-4 text-[#FFD700] mr-2" />
                  ) : (
                    <Circle className="h-4 w-4 text-[#F7E8CC]/50 mr-2" />
                  )}
                  {item.label}
                </button>
              ))}
              <div className="pt-2 mt-2 border-t border-[#4A110C]">
                <p className="text-[#FFD700] font-bold text-center">
                  {t.progress}: {completedItems.length}/7 {lang === 'es' ? 'Completo' : 'Complete'}
                </p>
              </div>
            </nav>
          </div>
        )}
      </div>

      {/* Main Content */}
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Welcome Section with Checklist */}
        <div className="bg-[#6E1912] rounded-lg p-6 mb-8">
          <h1 className="text-3xl font-bold text-[#FFD700] mb-4">
            {t.welcomeTitle}
          </h1>
          <p className="text-[#F7E8CC]/90 mb-6">
            {t.welcomeText}
          </p>

          {/* Interactive Checklist */}
          <div className="bg-[#5E150F] rounded-lg p-4">
            <h3 className="font-bold text-[#FFD700] mb-4">{t.yourJourney}</h3>
            <div className="space-y-2">
              {checklistItems.map(item => (
                <div
                  key={item.id}
                  className="flex items-center group"
                >
                  <button
                    onClick={() => toggleComplete(item.id)}
                    className="p-1 mr-2"
                  >
                    {completedItems.includes(item.id) ? (
                      <CheckCircle className="h-5 w-5 text-[#FFD700]" />
                    ) : (
                      <Circle className="h-5 w-5 text-[#F7E8CC]/50 hover:text-[#F7E8CC]" />
                    )}
                  </button>
                  <button
                    onClick={item.action}
                    className={`flex-1 text-left px-3 py-2 rounded hover:bg-[#4A110C]/30 transition-colors ${
                      completedItems.includes(item.id) ? 'line-through text-[#F7E8CC]/50' : 'text-[#F7E8CC]'
                    }`}
                  >
                    {item.label}
                    <ChevronRight className="inline ml-2 h-4 w-4 opacity-0 group-hover:opacity-100 transition-opacity" />
                  </button>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Account Setup Section */}
        <div ref={accountSetupRef} className="mb-12 scroll-mt-20">
          <div className="bg-[#6E1912] rounded-lg p-6">
            <h2 className="text-2xl font-bold text-[#FFD700] mb-4 flex items-center">
              <AlertCircle className="mr-3 h-6 w-6" />
              {t.step1Title}
            </h2>
            
            <div className="bg-[#FFD700]/20 border border-[#FFD700]/50 rounded-lg p-4 mb-6">
              <p className="text-[#F7E8CC] font-bold">
                {t.step1Important}
              </p>
            </div>

            <div className="space-y-6">
              {/* Account Creation */}
              <div className="bg-[#5E150F] rounded-lg p-5">
                <h3 className="font-bold text-[#FFD700] mb-3">{t.createAccounts}</h3>
                <p className="text-[#F7E8CC]/90 mb-4">
                  {t.createAccountsText}
                </p>
                <div className="grid md:grid-cols-2 gap-4">
                  <a href={`/tiktok-account-setup${lang === 'es' ? '?lang=es' : ''}`} className="bg-[#4A110C] p-4 rounded-lg hover:bg-[#3A0E09] transition-colors">
                    <h4 className="text-[#FFD700] font-semibold mb-2">{t.tiktokPlatform}</h4>
                    <p className="text-[#F7E8CC]/70 text-sm">{t.tiktokDesc}</p>
                  </a>
                  <a href={`/instagram-account-setup${lang === 'es' ? '?lang=es' : ''}`} className="bg-[#4A110C] p-4 rounded-lg hover:bg-[#3A0E09] transition-colors">
                    <h4 className="text-[#FFD700] font-semibold mb-2">{t.instagramPlatform}</h4>
                    <p className="text-[#F7E8CC]/70 text-sm">{t.instagramDesc}</p>
                  </a>
                </div>
              </div>

              {/* Account Warming */}
              <div className="bg-[#5E150F] rounded-lg p-5">
                <h3 className="font-bold text-[#FFD700] mb-3">{t.warmUpTitle}</h3>
                <p className="text-[#F7E8CC]/90 mb-4">
                  {t.warmUpText}
                </p>
                <ol className="space-y-3 text-[#F7E8CC]/90">
                  <li className="flex">
                    <span className="text-[#FFD700] font-bold mr-3">1.</span>
                    {getWarmUpStepContent(1)}
                  </li>
                  <li className="flex">
                    <span className="text-[#FFD700] font-bold mr-3">2.</span>
                    {getWarmUpStepContent(2)}
                  </li>
                  <li className="flex">
                    <span className="text-[#FFD700] font-bold mr-3">3.</span>
                    {getWarmUpStepContent(3)}
                  </li>
                  <li className="flex">
                    <span className="text-[#FFD700] font-bold mr-3">4.</span>
                    {getWarmUpStepContent(4)}
                  </li>
                </ol>
                
                <div className="mt-4 p-3 bg-[#4A110C] rounded-lg">
                  <p className="text-[#F7E8CC]/70 text-sm">
                    <strong className="text-[#FFD700]">{lang === 'es' ? 'Nota:' : 'Note:'}</strong> {t.warmUpNote}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Introduction Video Section */}
        <div ref={introVideoRef} className="mb-12 scroll-mt-20">
          <div className="bg-[#6E1912] rounded-lg overflow-hidden">
            <div className="p-6">
              <h2 className="text-2xl font-bold text-[#FFD700] mb-4 flex items-center">
                <PlayCircle className="mr-3 h-6 w-6" />
                {t.step2Title}
              </h2>
              <p className="text-[#F7E8CC]/90 mb-4">
                {t.step2Text}
              </p>
            </div>
            <div className="bg-black/50">
              <video controls className="w-full h-auto max-h-[500px] object-contain">
                <source src={introVideo} type="video/quicktime" />
                <source src={introVideo} type="video/mp4" />
                Your browser does not support the video tag.
              </video>
            </div>
          </div>
        </div>

        {/* Hook Formula Section */}
        <div ref={hooksFormulaRef} className="mb-12 scroll-mt-20">
          <div className="bg-[#6E1912] rounded-lg p-6">
            <h2 className="text-2xl font-bold text-[#FFD700] mb-6">
              {t.step3Title}
            </h2>
            
            <div className="bg-[#FFD700]/20 border border-[#FFD700]/50 rounded-lg p-4 mb-6">
              <p className="text-[#F7E8CC] font-bold text-center">
                {t.step3Important}
              </p>
            </div>

            <div className="grid md:grid-cols-3 gap-4 mb-6">
              <div className="bg-[#5E150F] rounded-lg p-4">
                <div className="text-3xl mb-3 text-center">👀</div>
                <h3 className="font-bold text-[#FFD700] mb-2">{t.visualHook}</h3>
                <p className="text-[#F7E8CC]/80 text-sm mb-3">
                  {t.visualHookDesc}
                </p>
                <ul className="text-[#F7E8CC]/70 text-xs space-y-1">
                  <li>• {lang === 'es' ? 'Dejar el teléfono dramáticamente' : 'Setting phone down dramatically'}</li>
                  <li>• {lang === 'es' ? 'Caminar por la puerta' : 'Walking through door'}</li>
                  <li>• {lang === 'es' ? 'Sentarse como derramando té' : 'Sitting down like spilling tea'}</li>
                </ul>
              </div>

              <div className="bg-[#5E150F] rounded-lg p-4">
                <div className="text-3xl mb-3 text-center">📝</div>
                <h3 className="font-bold text-[#FFD700] mb-2">{t.textHook}</h3>
                <p className="text-[#F7E8CC]/80 text-sm mb-3">
                  {t.textHookDesc}
                </p>
                <ul className="text-[#F7E8CC]/70 text-xs space-y-1">
                  <li>• {lang === 'es' ? 'Has sido advertido...' : "You've been warned..."}</li>
                  <li>• {lang === 'es' ? '¿Me están manipulando?' : "Am I being gaslit?"}</li>
                  <li>• {lang === 'es' ? 'Mi esposo NO PUEDE MANTENERLO EN SUS PANTALONES' : "My husband CAN'T KEEP IT IN HIS PANTS"}</li>
                </ul>
              </div>

              <div className="bg-[#5E150F] rounded-lg p-4">
                <div className="text-3xl mb-3 text-center">🎙️</div>
                <h3 className="font-bold text-[#FFD700] mb-2">{t.verbalHook}</h3>
                <p className="text-[#F7E8CC]/80 text-sm mb-3">
                  {t.verbalHookDesc}
                </p>
                <ul className="text-[#F7E8CC]/70 text-xs space-y-1">
                  <li>• {lang === 'es' ? 'Descubrí...' : "I found out..."}</li>
                  <li>• {lang === 'es' ? '¿Debería confrontar...' : "Should I confront..."}</li>
                  <li>• {lang === 'es' ? 'Mi esposo casi me despide...' : "My husband almost got me fired..."}</li>
                </ul>
              </div>
            </div>

            {/* Hooks Training Video */}
            <div className="bg-[#5E150F] rounded-lg overflow-hidden">
              <div className="p-4">
                <h3 className="font-bold text-[#FFD700] mb-2">{t.watchHooksTraining}</h3>
              </div>
              <div className="bg-black/50">
                <video controls className="w-full h-auto max-h-[400px] object-contain">
                  <source src={hooksVideo} type="video/mp4" />
                </video>
              </div>
            </div>
          </div>
        </div>

        {/* Training Videos Section */}
        <div ref={trainingVideosRef} className="mb-12 scroll-mt-20">
          <div className="bg-[#6E1912] rounded-lg p-6">
            <h2 className="text-2xl font-bold text-[#FFD700] mb-6">
              {t.step4Title}
            </h2>

            {/* CTA Video */}
            <div className="bg-[#5E150F] rounded-lg overflow-hidden mb-6">
              <div className="p-4">
                <h3 className="font-bold text-[#FFD700] mb-2">{t.ctaBestPractices}</h3>
                <p className="text-[#F7E8CC]/80 text-sm mb-2">
                  {t.ctaDesc}
                </p>
              </div>
              <div className="bg-black/50">
                <video controls className="w-full h-auto max-h-[400px] object-contain">
                  <source src={ctaVideo} type="video/mp4" />
                </video>
              </div>
            </div>

            {/* CTA Guidelines */}
            <div className="bg-[#5E150F] rounded-lg p-4">
              <p className="text-[#FF4136] font-bold mb-3">
                {t.ctaWarning}
              </p>
              <p className="text-[#F7E8CC]/90 mb-2 font-bold">{t.ctaInstead}</p>
              <ul className="text-[#F7E8CC]/80 text-sm space-y-1">
                <li>✓ {lang === 'es' ? 'Dulces para adultos para el dormitorio' : "Adult candy for the bedroom"}</li>
                <li>✓ {lang === 'es' ? 'Hacen efecto en unos 30 minutos y duran alrededor de 3 horas' : "They kick in about 30 mins and last around 3 hours"}</li>
                <li>✓ {lang === 'es' ? 'Lo mejor es que también funcionan en las mujeres' : "The best part is they actually work on women too"}</li>
              </ul>
            </div>
          </div>
        </div>

        {/* First Draft Section */}
        <div ref={firstDraftRef} className="mb-12 scroll-mt-20">
          <div className="bg-[#FFD700]/20 border-2 border-[#FFD700]/50 rounded-lg p-6">
            <h2 className="text-2xl font-bold text-[#FFD700] mb-4">
              {t.step5Title}
            </h2>
            <div className="flex items-start">
              <AlertCircle className="h-6 w-6 text-[#FFD700] mr-3 mt-1 flex-shrink-0" />
              <div>
                <p className="text-[#F7E8CC] mb-4">
                  <strong>{lang === 'es' ? 'IMPORTANTE:' : 'IMPORTANT:'}</strong> {t.step5Important}
                </p>
                <ol className="text-[#F7E8CC]/90 space-y-2 list-decimal list-inside">
                  {t.step5Steps.map((step: string, index: number) => (
                    <li key={index}>{step}</li>
                  ))}
                </ol>
                <div className="mt-4 flex flex-col sm:flex-row gap-3">
                  <a 
                    href="https://docs.google.com/document/d/1BUZY-DdhM1PzTQZNFHF0ejnoRNvAel3CNnw9m6RAazE/edit?tab=t.0"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center justify-center px-4 py-2 bg-[#FFD700] text-[#7B1C14] font-bold rounded hover:bg-[#FFD700]/90"
                  >
                    {t.getScriptTemplates}
                    <ChevronRight className="ml-2 h-4 w-4" />
                  </a>
                  <button className="inline-flex items-center justify-center px-4 py-2 border border-[#FFD700] text-[#FFD700] font-bold rounded hover:bg-[#FFD700]/10">
                    {t.contactAshliSlack}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Posting Requirements Section */}
        <div ref={postingRef} className="mb-12 scroll-mt-20">
          <div className="bg-[#6E1912] rounded-lg p-6">
            <h2 className="text-2xl font-bold text-[#FFD700] mb-6">
              {t.step6Title}
            </h2>

            <div className="grid md:grid-cols-2 gap-6">
              <div className="bg-[#5E150F] rounded-lg p-4">
                <h3 className="font-bold text-[#FFD700] mb-3">{t.minRequirements}</h3>
                <ul className="space-y-2 text-[#F7E8CC]/90">
                  {t.minRequirementsList.map((item: string, index: number) => (
                    <li key={index} className="flex items-center">
                      <CheckCircle className="h-4 w-4 text-[#FFD700] mr-2" />
                      {item}
                    </li>
                  ))}
                </ul>
              </div>

              <div className="bg-[#5E150F] rounded-lg p-4">
                <h3 className="font-bold text-[#FFD700] mb-3">{t.maxEarnings}</h3>
                <ul className="space-y-2 text-[#F7E8CC]/90">
                  {t.maxEarningsList.map((item: string, index: number) => (
                    <li key={index} className="flex items-center">
                      <ChevronRight className="h-4 w-4 text-[#FFD700] mr-2" />
                      {item}
                    </li>
                  ))}
                </ul>
              </div>
            </div>

            {/* Platform Setup Links */}
            <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 gap-3">
              <a 
                href={`/tiktok-account-setup${lang === 'es' ? '?lang=es' : ''}`}
                className="px-4 py-2 bg-[#FF4136] text-[#F7E8CC] text-center font-bold rounded hover:bg-[#FF2718]"
              >
                {lang === 'es' ? 'Configuración TikTok' : 'TikTok Setup'}
              </a>
              <a 
                href={`/instagram-account-setup${lang === 'es' ? '?lang=es' : ''}`}
                className="px-4 py-2 bg-[#FF851B] text-[#F7E8CC] text-center font-bold rounded hover:bg-[#FF6700]"
              >
                {lang === 'es' ? 'Configuración Instagram' : 'Instagram Setup'}
              </a>
            </div>

            {/* Content Guidelines */}
            <div className="mt-6 bg-[#5E150F] rounded-lg p-5">
              <h3 className="font-bold text-[#FFD700] mb-4">{t.contentGuidelines}</h3>
              
              <div className="space-y-4">
                {/* Tone */}
                <div>
                  <h4 className="font-semibold text-[#F7E8CC] mb-2">{t.tone}</h4>
                  <p className="text-[#F7E8CC]/90 text-sm">
                    {t.toneDesc}
                  </p>
                </div>

                {/* Restricted Words */}
                <div>
                  <h4 className="font-semibold text-[#F7E8CC] mb-2">{t.restrictedWords}</h4>
                  <div className="bg-[#4A110C] rounded p-3">
                    <p className="text-[#FFD700] font-bold text-sm mb-2">{t.restrictedWordsWarning}</p>
                    <p className="text-[#F7E8CC]/90 text-sm">
                      {t.restrictedWordsList}
                    </p>
                  </div>
                </div>

                {/* Alternative Words */}
                <div>
                  <h4 className="font-semibold text-[#F7E8CC] mb-2">{t.alternativeWords}</h4>
                  <p className="text-[#F7E8CC]/90 text-sm">
                    {t.alternativeWordsList}
                  </p>
                </div>

                {/* Community Guidelines */}
                <div>
                  <h4 className="font-semibold text-[#F7E8CC] mb-2">{t.communityGuidelines}</h4>
                  <ul className="space-y-2 text-[#F7E8CC]/90 text-sm">
                    {t.communityGuidelinesList.map((guideline: string, index: number) => {
                      const parts = guideline.split(':');
                      return (
                        <li key={index} className="flex items-start">
                          <span className="text-[#FFD700] mr-2">•</span>
                          <div>
                            <strong>{parts[0]}:</strong> {parts[1]}
                          </div>
                        </li>
                      );
                    })}
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Engagement Section */}
        <div ref={engagementRef} className="mb-12 scroll-mt-20">
          <div className="bg-[#6E1912] rounded-lg p-6">
            <h2 className="text-2xl font-bold text-[#FFD700] mb-6">
              {t.step7Title}
            </h2>

            <div className="bg-[#5E150F] rounded-lg p-4 mb-4">
              <h3 className="font-bold text-[#FFD700] mb-3">{t.platformTips}</h3>
              <div className="space-y-3">
                <div>
                  <p className="font-bold text-[#F7E8CC]">TikTok:</p>
                  <p className="text-[#F7E8CC]/80 text-sm">{t.tiktokTip}</p>
                </div>
                <div>
                  <p className="font-bold text-[#F7E8CC]">Instagram:</p>
                  <p className="text-[#F7E8CC]/80 text-sm">{t.instagramTip}</p>
                </div>
              </div>
            </div>

            <p className="text-[#F7E8CC]/90 text-center">
              {t.engagementReminder}
            </p>
          </div>
        </div>

        {/* Contact Section */}
        <div className="bg-[#FFD700]/20 border border-[#FFD700]/50 rounded-lg p-6 text-center">
          <h3 className="font-bold text-[#FFD700] mb-3">{t.needHelp}</h3>
          <p className="text-[#F7E8CC] mb-4">
            {t.needHelpText}
          </p>
          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            <a 
              href="mailto:ashli@spicycubes.co"
              className="inline-flex items-center justify-center px-6 py-2 bg-[#FFD700] text-[#7B1C14] font-bold rounded hover:bg-[#FFD700]/90"
            >
              {t.emailAshli}
            </a>
            <button className="inline-flex items-center justify-center px-6 py-2 border border-[#FFD700] text-[#FFD700] font-bold rounded hover:bg-[#FFD700]/10">
              {t.messageSlack}
            </button>
          </div>
        </div>
      </div>

      {/* Back to Top Button */}
      <button
        onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
        className="fixed bottom-6 right-6 p-3 bg-[#FFD700] text-[#7B1C14] rounded-full shadow-lg hover:bg-[#FFD700]/90 transition-colors"
        aria-label="Back to top"
      >
        <ArrowUp className="h-5 w-5" />
      </button>
    </div>
  );
}

export const ContentBriefUnified = ContentBriefUnifiedComponent;